import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import client from "./Apollo";
import { ThemeProvider } from "./theme/ThemeProvider";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./Components/ErrorBoundary";
import LoginPage from "./Pages/LoginPage";
import DashboardLayout from "./Layouts/DashboardLayout";
import DashboardOrCouponPage from "./Pages/DashboardOrCouponPage";
import MyVisitorsPage from "./Pages/MyVisitorsPage";
import ScanningVisitorsPage from "./Pages/ScanningVisitorsPage";
import InsightPage from "./Pages/InsightPage";
import AddStaffPage from "./Pages/AddStaffPage";
import SettingsPage from "./Pages/SettingsPage";
import StoragePage from "./Pages/StoragePage";
import EmailTemplatesPage from "./Pages/EmailTemplatesPage";
import EmailSettingsPage from "./Pages/EmailSettingsPage";
import AttendeeManagementPage from "./Pages/AttendeeManagementPage";
import CouponProgressReportPage from "./Pages/CouponProgressReportPage";
import ClaimCouponPage from "./Pages/ClaimCouponPage";
import JotunShopRegistrationPage from "./Pages/JotunShopRegistrationPage";

// Layouts

// Pages

// Enhanced Auth Guard Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function AppContent() {
  // Force client to refresh token on auth changes
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Refresh Apollo client authorization header when auth state changes
    if (isAuthenticated) {
      client.resetStore();
    }
  }, [isAuthenticated]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/jotun-shop-registration" element={<JotunShopRegistrationPage />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardOrCouponPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardOrCouponPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-visitors"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MyVisitorsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/scanning-visitors"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ScanningVisitorsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/claim-coupon"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ClaimCouponPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <InsightPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/staff"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AddStaffPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/storage"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StoragePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EmailTemplatesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/email-settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EmailSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/attendees"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AttendeeManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/coupon-progress"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CouponProgressReportPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === "development"}>
      <ApolloProvider client={client}>
        <ThemeProvider>
          <Router>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
}

export default App;
